import {
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Alert,
  Collapse,
  CircularProgress,
} from "@mui/material";
import { useRef, useState } from "react";

import CustomButton from "./CustomShapedBtn";
import AttachmentPreview from "./AttachmentPreview";
import { ticketPriority } from "./action";

const NewTicketForm = ({ onCancel, onSubmit }) => {
  const [title, setTitle] = useState("");
  const [priority, setPriority] = useState("low");
  const [description, setDescription] = useState("");
  const [attachment, setAttachment] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form validation states
  const [errors, setErrors] = useState({});
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertSeverity, setAlertSeverity] = useState("error");

  const fileInputRef = useRef(null);

  const handleFileUpload = (event) => {
    const file = event.target.files[0]; // Only take the first file
    if (!file) return;

    // Check if file is an image
    if (!file.type.startsWith("image/")) {
      setAlertSeverity("error");
      setAlertMessage("Only image files are allowed");
      setShowAlert(true);
      return;
    }

    // Check file size (limit to 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB in bytes
    if (file.size > maxSize) {
      setAlertSeverity("error");
      setAlertMessage("Image size should be less than 5MB");
      setShowAlert(true);
      return;
    }

    const newAttachment = {
      id: Math.random().toString(36).substr(2, 9),
      name: file.name,
      type: file.type,
      size: file.size,
      file: file, // Store the actual File object
      preview: URL.createObjectURL(file),
    };

    setAttachment(newAttachment);
  };

  const removeAttachment = () => {
    setAttachment(null);
  };

  const discardChanges = () => {
    setTitle("");
    // setCategory("");
    setPriority("low");
    setDescription("");
    setAttachment(null);
    setErrors({});
    setShowAlert(false);
    onCancel();
  };

  // Field validation helper functions
  const validateTitle = (value) => {
    if (!value.trim()) return "Title is required";
    if (value.trim().length < 5) return "Title must be at least 5 characters";
    return "";
  };

  const validateDescription = (value) => {
    if (!value.trim()) return "Description is required";
    if (value.trim().length < 20)
      return "Description must be at least 20 characters";
    return "";
  };

  // Form validation
  const validateForm = () => {
    const newErrors = {
      title: validateTitle(title),
      description: validateDescription(description),
    };

    setErrors(newErrors);

    // Check if there are any errors
    return !Object.values(newErrors).some((error) => error !== "");
  };

  // Handle form submission
  const handleSubmit = async () => {
    setShowAlert(false);

    if (validateForm()) {
      try {
        setIsSubmitting(true);

        // Create FormData object for submission
        const formData = new FormData();

        // Add text fields
        formData.append("title", title.trim());
        formData.append("priority", priority);
        formData.append("description", description.trim());

        // Append image if exists
        if (attachment && attachment.file) {
          // Use the correct field name that the server expects in req.files.image
          // The key name here is important - it must match what the server expects
          formData.append("image", attachment.file, attachment.file.name);
        }

        // Call the onSubmit handler passed from parent component with FormData
        await onSubmit(formData);

        // Show success message
        setAlertSeverity("success");
        setAlertMessage("Ticket submitted successfully!");
        setShowAlert(true);
        onCancel(); // Close the form

        // Reset form (optional, depends on UX requirements)
        // discardChanges();
      } catch (error) {
        // Handle any submission errors
        setAlertSeverity("error");
        setAlertMessage(`Error submitting ticket: ${error.message}`);
        setShowAlert(true);
      } finally {
        setIsSubmitting(false);
      }
    } else {
      // Show validation error message
      setAlertSeverity("error");
      setAlertMessage("Please correct the errors in the form");
      setShowAlert(true);
    }
  };

  return (
    <Card
      variant="outlined"
      sx={{
        mb: 3,
        backgroundColor: "#212121", // Dark gray card background
        color: "#D8D5CD", // Text color
        borderRadius: "16px",
        border: "1px solid #474747",
      }}
    >
      <CardContent>
        <Typography variant="h6" gutterBottom sx={{ color: "#D8D5CD" }}>
          Create New Ticket
        </Typography>

        {/* Alert for validation errors or success messages */}
        <Collapse in={showAlert}>
          <Alert
            severity={alertSeverity}
            sx={{ mb: 2 }}
            onClose={() => setShowAlert(false)}
          >
            {alertMessage}
          </Alert>
        </Collapse>

        <Box component="form" sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Title"
                placeholder="Brief description of your issue"
                variant="outlined"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                error={!!errors.title}
                helperText={errors.title}
                InputLabelProps={{ style: { color: "#D8D5CD" } }} // Label color
                FormHelperTextProps={{ style: { color: "#ff7171" } }} // Error text color
                sx={{
                  input: { color: "#D8D5CD" }, // Text color
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      borderColor: errors.title ? "#ff5252" : "#D8D5CD",
                    }, // Border color
                    "&:hover fieldset": { borderColor: "gray" }, // Hover effect
                    "&.Mui-focused fieldset": {
                      borderColor: "lightblue",
                    }, // Focus effect
                  },
                  backgroundColor: "#000",
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth variant="outlined">
                <InputLabel id="priority-label" sx={{ color: "#D8D5CD" }}>
                  Priority
                </InputLabel>
                <Select
                  labelId="priority-label"
                  label="Priority"
                  value={priority}
                  onChange={(e) => setPriority(e.target.value)}
                  defaultValue="low"
                  sx={{
                    color: "#D8D5CD",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#D8D5CD",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "gray",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "lightblue",
                    },
                    backgroundColor: "#000",
                  }}
                >
                  {ticketPriority.map((priority) => (
                    <MenuItem key={priority.id} value={priority.name}>
                      {priority.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                placeholder="Please provide details about your issue..."
                variant="outlined"
                multiline
                rows={4}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                error={!!errors.description}
                helperText={errors.description}
                InputLabelProps={{ style: { color: "#D8D5CD" } }}
                FormHelperTextProps={{ style: { color: "#ff7171" } }}
                sx={{
                  textarea: { color: "#D8D5CD" }, // Text color
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      borderColor: errors.description ? "#ff5252" : "#D8D5CD",
                    },
                    "&:hover fieldset": { borderColor: "gray" },
                    "&.Mui-focused fieldset": {
                      borderColor: "lightblue",
                    },
                  },
                  backgroundColor: "#000",
                }}
              />
            </Grid>

            {attachment && (
              <Grid item xs={12}>
                <AttachmentPreview
                  attachments={[attachment]}
                  onRemoveAttachment={removeAttachment}
                />
              </Grid>
            )}

            <Grid item xs={12}>
              <input
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                ref={fileInputRef}
                onChange={handleFileUpload}
              />
              <CustomButton
                variant="attachment"
                title={attachment ? "Change Image" : "Upload Image"}
                onClick={() => fileInputRef.current.click()}
              />
              {attachment && (
                <Typography variant="caption" sx={{ ml: 2, color: "#D8D5CD" }}>
                  One image file supported
                </Typography>
              )}
            </Grid>

            <Grid item xs={12}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 2,
                  mt: 1,
                }}
              >
                <Button
                  variant="text"
                  onClick={discardChanges}
                  sx={{ color: "#D8D5CD" }}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <CustomButton
                  variant="primary"
                  title={
                    isSubmitting ? (
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <CircularProgress
                          size={20}
                          sx={{ mr: 1, color: "inherit" }}
                        />
                        Submitting...
                      </Box>
                    ) : (
                      "Submit Ticket"
                    )
                  }
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                />
              </Box>
            </Grid>
          </Grid>
        </Box>
      </CardContent>
    </Card>
  );
};

export default NewTicketForm;
